import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { 
  useAllMonitoringData, 
  useWebSocketConnectionStatus
} from '../hooks/useWebSocketData';
import { monitoringWebSocket } from '../services/websocketService';
import { WS_ORIGIN } from '../config';
import { Alert, WaterQuality, SafetyStatus, Cctv, Wearable } from '../types/domain';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from './AuthContext';
import { extractPoolCodeFromPathname } from '../components/monitoring/cctvPopupRoutes';
import axios from '../utils/axios';

// WebSocket 컨텍스트 타입 정의 확장
interface WebSocketContextType {
  alerts: Alert | null;
  waterQuality: WaterQuality | null;
  safetyStatus: SafetyStatus | null;
  cctvStatus: Cctv | null;
  wearableStatus: Wearable | null;
  isLoading: boolean;
  error: string | null;
  refreshAll: () => void;
  fetchAllPoolData: (() => boolean) | undefined;
  connectionStatus: 'disconnected' | 'connecting' | 'connected';
  reconnect: (poolId?: string | number) => void;
  connectionInfo: {
    uptime: number;
    reconnectAttempts: number;
    lastAttemptTime: Date | null;
  };
  setAutoReconnect: (enabled: boolean) => void;
  isAutoReconnectEnabled: boolean;
}

// 초기 컨텍스트 값 설정
const initialContext: WebSocketContextType = {
  alerts: null,
  waterQuality: null,
  safetyStatus: null,
  cctvStatus: null,
  wearableStatus: null,
  isLoading: true,
  error: null,
  refreshAll: () => {},
  fetchAllPoolData: undefined,
  connectionStatus: 'disconnected',
  reconnect: () => {},
  connectionInfo: {
    uptime: 0,
    reconnectAttempts: 0,
    lastAttemptTime: null
  },
  setAutoReconnect: () => {},
  isAutoReconnectEnabled: true
};

// WebSocket 컨텍스트 생성
const WebSocketContext = createContext<WebSocketContextType>(initialContext);

// WebSocket Provider 속성 정의
interface WebSocketProviderProps {
  children: ReactNode;
}

// WebSocket Provider 컴포넌트 확장
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const location = useLocation();
  const poolCodeFromPath = extractPoolCodeFromPathname(location.pathname);
  const isCentralMonitoring =
    location.pathname === '/central-monitoring' ||
    location.pathname === '/individual-monitoring' ||
    location.pathname === '/all/cctv';
  const isCentralControl = location.pathname === '/central-control';
  const useControlCenterWs = isCentralMonitoring || isCentralControl;
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated, loading: authLoading } = useAuth();
  /** 브로드캐스트 키는 숫자 pool_id — 코드 문자열로 붙으면 이벤트를 못 받음 */
  const [resolvedPoolWsId, setResolvedPoolWsId] = useState<number | null>(null);
  
  // 자동 재연결 상태
  const [isAutoReconnectEnabled, setIsAutoReconnectEnabled] = useState(true);
  
  // 연결 정보 상태
  const [connectionInfo, setConnectionInfo] = useState({
    uptime: 0,
    reconnectAttempts: 0,
    lastAttemptTime: null as Date | null
  });
  
  // 연결 상태 변경 감지
  const prevConnectionStatusRef = useRef<'disconnected' | 'connecting' | 'connected'>('disconnected');
  /** 끊김이 지속될 때만 스낵바 (짧은 재연결 깜빡임은 무시) */
  const disconnectToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectToastShownRef = useRef(false);
  const SUSTAINED_DISCONNECT_MS = 12000;

  const clearDisconnectToastTimer = useCallback(() => {
    if (disconnectToastTimerRef.current) {
      clearTimeout(disconnectToastTimerRef.current);
      disconnectToastTimerRef.current = null;
    }
  }, []);
  
  // 업타임 및 연결 정보 주기적 업데이트
  useEffect(() => {
    const updateConnectionInfo = () => {
      setConnectionInfo({
        uptime: monitoringWebSocket.getUptimeSeconds() || 0,
        reconnectAttempts: monitoringWebSocket.getReconnectAttempts() || 0,
        lastAttemptTime: monitoringWebSocket.getLastAttemptTime()
      });
    };
    
    const infoInterval = setInterval(updateConnectionInfo, 1000);
    return () => clearInterval(infoInterval);
  }, []);
  
  // 자동 재연결 설정
  const setAutoReconnect = useCallback((enabled: boolean) => {
    setIsAutoReconnectEnabled(enabled);
    if (enabled) {
      monitoringWebSocket.enableWebSocket();
      enqueueSnackbar('자동 재연결이 활성화됨', { variant: 'success' });
    } else {
      enqueueSnackbar('자동 재연결이 비활성화됨. 필요시 수동으로 연결', { variant: 'info' });
    }
  }, [enqueueSnackbar]);
  
  useEffect(() => {
    if (!poolCodeFromPath) {
      setResolvedPoolWsId(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await axios.get(`/api/pools/code/${encodeURIComponent(poolCodeFromPath)}`);
        const id = Number(res?.data?.id);
        if (!cancelled && Number.isFinite(id) && id > 0) {
          setResolvedPoolWsId(id);
        }
      } catch {
        if (!cancelled) setResolvedPoolWsId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolCodeFromPath]);

  // 시설 화면: 숫자 pool_id 로 /pool/{id} 연결. 관제: control-center
  useEffect(() => {
    if (authLoading) return;

    if (resolvedPoolWsId != null) {
      console.log(`WebSocketProvider: Connecting to pool id ${resolvedPoolWsId}`);
      monitoringWebSocket.connectToPool(resolvedPoolWsId);
    } else if (useControlCenterWs && isAuthenticated) {
      console.log('WebSocketProvider: Connecting to control-center');
      monitoringWebSocket.connectToControlCenter();
    }

    return () => {
      // 컴포넌트가 언마운트될 때 명시적으로 연결 해제 안 함
      // 다른 컴포넌트에서 재사용할 수 있도록
    };
  }, [resolvedPoolWsId, useControlCenterWs, isAuthenticated, authLoading]);
  
  // useAllMonitoringData 훅을 사용하여 모든 실시간 데이터 구독
  const {
    alerts,
    waterQuality,
    safetyStatus,
    cctvStatus,
    wearableStatus,
    isLoading,
    error,
    refreshAll,
    fetchAllPoolData,
    connectionStatus
  } = useAllMonitoringData(
    resolvedPoolWsId != null ? String(resolvedPoolWsId) : poolCodeFromPath || undefined
  );
  
  // WebSocket(백엔드 실시간) — 짧은 끊김/재연결 토스트는 내지 않음.
  // disconnected 가 SUSTAINED_DISCONNECT_MS 이상 유지될 때만 경고.
  // 복구 토스트는 "지속 끊김 경고를 이미 낸 뒤"에만 1회.
  useEffect(() => {
    if (prevConnectionStatusRef.current === connectionStatus) {
      return;
    }

    if (connectionStatus === 'connected') {
      clearDisconnectToastTimer();
      if (disconnectToastShownRef.current) {
        enqueueSnackbar('실시간 모니터링(WebSocket) 연결이 복구됨', {
          variant: 'success',
          preventDuplicate: true,
        });
        disconnectToastShownRef.current = false;
      }
    } else if (connectionStatus === 'disconnected') {
      // 재연결 깜빡임(connecting↔disconnected)으로 타이머를 리셋하지 않음
      if (!disconnectToastTimerRef.current && !disconnectToastShownRef.current) {
        disconnectToastTimerRef.current = setTimeout(() => {
          disconnectToastTimerRef.current = null;
          if (monitoringWebSocket.getConnectionStatus() === 'connected') {
            return;
          }
          disconnectToastShownRef.current = true;
          enqueueSnackbar(
            '실시간 모니터링(WebSocket) 연결이 끊긴 상태가 지속됨. 재연결을 시도.',
            { variant: 'warning', preventDuplicate: true, persist: false },
          );
        }, SUSTAINED_DISCONNECT_MS);
      }
    }
    // connecting: leave sustained-disconnect timer running

    prevConnectionStatusRef.current = connectionStatus;
  }, [connectionStatus, enqueueSnackbar, clearDisconnectToastTimer]);

  useEffect(() => () => clearDisconnectToastTimer(), [clearDisconnectToastTimer]);
  
  // 수동 재연결 — 하단 토스트 없이 조용히 재시도 (상태는 상단 아이콘으로 확인)
  const reconnect = useCallback((poolId?: string | number) => {
    if (poolId) {
      monitoringWebSocket.connectToPool(poolId);
    } else if (resolvedPoolWsId != null) {
      monitoringWebSocket.connectToPool(resolvedPoolWsId);
    } else if (useControlCenterWs) {
      monitoringWebSocket.connectToControlCenter();
    } else if (monitoringWebSocket.getConnectionStatus() !== 'connected') {
      monitoringWebSocket.enableWebSocket();
      monitoringWebSocket.connect(WS_ORIGIN);
    }
  }, [resolvedPoolWsId, useControlCenterWs]);
  
  // 컨텍스트에서 제공할 값
  const value: WebSocketContextType = {
    alerts,
    waterQuality,
    safetyStatus,
    cctvStatus,
    wearableStatus,
    isLoading,
    error,
    refreshAll,
    fetchAllPoolData,
    connectionStatus,
    reconnect,
    connectionInfo,
    setAutoReconnect,
    isAutoReconnectEnabled
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// 컨텍스트 사용을 위한 훅
export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

// 연결 상태만 가져오는 간단한 훅
export const useWebSocketConnection = () => {
  const { connectionStatus, reconnect } = useWebSocketContext();
  return { 
    connectionStatus, 
    isConnected: connectionStatus === 'connected',
    isConnecting: connectionStatus === 'connecting',
    isDisconnected: connectionStatus === 'disconnected',
    reconnect 
  };
};

// 특정 데이터 타입에 대한 접근을 위한 편의 훅들
export const useAlertsData = () => {
  const { alerts, isLoading, error, refreshAll } = useWebSocketContext();
  return { alerts, isLoading, error, refresh: refreshAll };
};

export const useWaterQualityContextData = () => {
  const { waterQuality, isLoading, error, refreshAll } = useWebSocketContext();
  return { waterQuality, isLoading, error, refresh: refreshAll };
};

export const useSafetyStatusContextData = () => {
  const { safetyStatus, isLoading, error, refreshAll } = useWebSocketContext();
  return { safetyStatus, isLoading, error, refresh: refreshAll };
};

export const useCctvStatusContextData = () => {
  const { cctvStatus, isLoading, error, refreshAll } = useWebSocketContext();
  return { cctvStatus, isLoading, error, refresh: refreshAll };
};

export const useWearableStatusContextData = () => {
  const { wearableStatus, isLoading, error, refreshAll } = useWebSocketContext();
  return { wearableStatus, isLoading, error, refresh: refreshAll };
}; 